/**
 * The vocabulary of the Swap page.
 *
 * Amounts are `bigint` everywhere in state and become `ethers.BigNumber` only
 * at the contract boundary. A `number` cannot hold 1e18 without losing the
 * last digits, and the last digits are the ones a slippage check is made of.
 */

export type Venue = 'aero-v2' | 'aero-cl' | 'aero-cl3' | 'uni-v3' | 'uni-v2' | 'kyber'

/** Every venue we can discover pools for. KyberSwap quotes itself. */
export type OnChainVenue = Exclude<Venue, 'kyber'>

export interface SwapToken {
  /** Lowercase. `NATIVE_SENTINEL` for ETH. */
  address: string
  symbol: string
  decimals: number
  logoURI?: string
  isNative: boolean
  isMpro: boolean
}

/** One pool the page may route through; discovered, then cached per session. */
export interface PoolRef {
  venue: OnChainVenue
  address: string
  /** Lowercase, sorted the way the factory sorts. */
  token0: string
  token1: string
  /** aero-v2 only. */
  stable?: boolean
  /** uni-v3 only: 100 | 500 | 3000 | 10000. */
  fee?: number
  /** aero-cl and aero-cl3 only; aero-cl3 adds 500. */
  tickSpacing?: number
}

/** A candidate route inside one venue: one or two pools, token path aligned to them. */
export interface RouteCandidate {
  venue: OnChainVenue
  /** `[tokenIn, (hub,) tokenOut]` — WETH stands in for ETH. */
  path: string[]
  /** `path.length - 1` entries. */
  pools: PoolRef[]
}

export interface KyberCandidate {
  venue: 'kyber'
}

export interface Quote {
  candidate: RouteCandidate | KyberCandidate
  amountIn: bigint
  amountOut: bigint
  /**
   * What the same route returns for a thousandth of `amountIn`. It is the
   * price the pool would give a trade too small to move it, and the ratio
   * between the two is the price impact — measured rather than modelled, so
   * it is right for a concentrated pool as well as a constant-product one.
   */
  amountOutAtReferencePrice?: bigint
  /** 0..10000. Undefined when the reference quote failed. */
  priceImpactBps?: number
  kyber?: { routeSummary: unknown; routerAddress: string }
  /**
   * When the platform router is in the path (scope 16): what the user pays in
   * total, and the platform's cut of it. `amountIn` stays "what the venue
   * receives", so every adapter and every quote keeps its meaning.
   */
  grossAmountIn?: bigint
  feeIn?: bigint
  feeBps?: number
}

export interface SwapSettings {
  /** Default 50 (0.50%); allowed 10..5000. */
  slippageBps: number
  deadlineMinutes: number
}

export const NATIVE_SENTINEL = '0x0000000000000000000000000000000000000000'
/** ≥ 5%: amber warning. */
export const IMPACT_WARN_BPS = 500
/** ≥ 15%: red, and the button stays disabled until the user ticks the box. */
export const IMPACT_CONFIRM_BPS = 1500

export const DEFAULT_SETTINGS: SwapSettings = { slippageBps: 50, deadlineMinutes: 20 }
export const MIN_SLIPPAGE_BPS = 10
export const MAX_SLIPPAGE_BPS = 5000
